import type { CSSProperties } from 'react';

/**
 * Font faces.
 *
 * The artboards load both families from Google Fonts in their `<helmet>`:
 * `Sora:wght@600;700;800` and `Public+Sans:wght@400;500;600;700`. The same two
 * stacks, in the same order, are what `theme/tokens.css` declares. Sora is the
 * display face and Public Sans is the text face; nothing in the design uses a
 * third, and the two monospace uses are the browser's own stack.
 */
export const fonts = {
  display: "'Sora', sans-serif",
  text: "'Public Sans', system-ui, sans-serif",
  mono: 'ui-monospace, Menlo, monospace',
} as const;

/**
 * The desktop type scale.
 *
 * **Derived, not invented.** Every `style` attribute in the 18 artboards on
 * page-2 of the canvas was parsed and grouped by (family, size, weight,
 * letter-spacing, transform). A combination became a step here only where
 * **three or more artboards** draw it. Twenty-one combinations met that test
 * and all twenty-one are below; nothing was dropped and nothing was added. The
 * census is in docs/DECISIONS.md D2.
 *
 * The rule is the one `veriplus-mobile-app/src/theme/typography.ts` used to
 * close its D31, and it is here for the same reason: a scale that is smaller
 * than the design forces screens to drift off it one hard-coded size at a time.
 *
 * **These styles carry no colour**, exactly as the mobile scale carries none.
 * Colour is a role, from `tokens.ts`, chosen at the use site.
 *
 * Where a step matches the mobile app's, it keeps the mobile app's name:
 * `cardTitle`, `captionSm`. Where the two products genuinely differ, the name
 * says which surface it belongs to rather than pretending they are the same.
 */
export const type = {
  /** Sora 800 / 26, the page title. 16 artboards, one per screen. */
  pageTitle: {
    fontFamily: fonts.display,
    fontSize: '26px',
    fontWeight: 800,
    letterSpacing: '-0.4px',
  },
  /** Sora 800 / 28, the number in a stat card. 6 artboards, 22 uses. */
  statValue: { fontFamily: fonts.display, fontSize: '28px', fontWeight: 800 },
  /** Sora 800 / 24, the compact stat number. 3 artboards, 5 uses. */
  statValueSm: { fontFamily: fonts.display, fontSize: '24px', fontWeight: 800 },
  /** Sora 700 / 16, the panel heading. 9 artboards. */
  panelTitle: { fontFamily: fonts.display, fontSize: '16px', fontWeight: 700 },
  /**
   * Sora 700 / 15, the card and row title.
   *
   * The same step, by the same name, as the mobile app's `cardTitle`. 16
   * artboards here, 6 there: the most agreed-on Sora size in the whole system.
   */
  cardTitle: { fontFamily: fonts.display, fontSize: '15px', fontWeight: 700 },
  /** Sora 700 / 14, the button label. 17 artboards, 50 uses. */
  button: { fontFamily: fonts.display, fontSize: '14px', fontWeight: 700 },

  /** Public Sans 400 / 14, the reading size on desktop. 18 artboards. */
  body: { fontFamily: fonts.text, fontSize: '14px', fontWeight: 400 },
  /** Public Sans 600 / 14, the row title and the resting nav label. 16 artboards. */
  bodyStrong: { fontFamily: fonts.text, fontSize: '14px', fontWeight: 600 },
  /** Public Sans 700 / 14, the selected nav label and the emphatic row. 16 artboards. */
  bodyBold: { fontFamily: fonts.text, fontSize: '14px', fontWeight: 700 },

  /** Public Sans 400 / 13, secondary and metadata text. 17 artboards. */
  meta: { fontFamily: fonts.text, fontSize: '13px', fontWeight: 400 },
  /** Public Sans 600 / 13. 7 artboards. */
  metaStrong: { fontFamily: fonts.text, fontSize: '13px', fontWeight: 600 },
  /** Public Sans 700 / 13, the name in a sidebar footer or a dense row. 16 artboards. */
  metaBold: { fontFamily: fonts.text, fontSize: '13px', fontWeight: 700 },

  /** Public Sans 400 / 12. 13 artboards. */
  caption: { fontFamily: fonts.text, fontSize: '12px', fontWeight: 400 },
  /** Public Sans 600 / 12. 7 artboards. */
  captionStrong: { fontFamily: fonts.text, fontSize: '12px', fontWeight: 600 },
  /** Public Sans 700 / 12, the delta line under a stat. 15 artboards, 68 uses. */
  captionBold: { fontFamily: fonts.text, fontSize: '12px', fontWeight: 700 },
  /** Public Sans 600 / 12, 0.08em, uppercase: the stat-card label. 10 artboards. */
  statLabel: {
    fontFamily: fonts.text,
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  /** Public Sans 700 / 12, 0.06em, uppercase: the table column header. 8 artboards. */
  tableHeader: {
    fontFamily: fonts.text,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },

  /**
   * Public Sans 400 / 11.
   *
   * The mobile app's `captionSm`, and the step `Main.dc.html` says should not
   * exist: its body-type rule bounds captions at "12-13 is for metadata
   * captions only". 18 artboards draw it here and 6 there. The band is what is
   * wrong, and the mobile app's D43 already records that. Nothing new to decide.
   */
  captionSm: { fontFamily: fonts.text, fontSize: '11px', fontWeight: 400 },
  /** Public Sans 700 / 11, the count badge on a nav row. 16 artboards. */
  badge: { fontFamily: fonts.text, fontSize: '11px', fontWeight: 700 },

  /**
   * Public Sans 700 / 10, 0.14em: the surface eyebrow under the wordmark.
   *
   * 16 artboards, exactly one use each, and it is the string that names which
   * system you are looking at: "MANUFACTURER PORTAL" or "STAFF CONSOLE". It is
   * the smallest type in the design and the only step below 11.
   */
  eyebrow: {
    fontFamily: fonts.text,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.14em',
  },
} satisfies Record<string, CSSProperties>;

export type TypeStep = keyof typeof type;
